import { Controller } from '@hotwired/stimulus'

/*
This controller exists for CameraImageFieldComponent, but is written to be applicable
to any HTML file field that needs to be styled in an arbitrary way. The expectation
is that a hidden input[type='file'] exists in the markup.
The "trigger" target is what triggers the file selector to open, since the underyling
input is assumed to be hidden.
If a "content" target is specified, its contents will get replaced with the HTML from
either the "defaultTemplate" or the "successTemplate" targets, depending on whether a
file has been selected.
If a "preview" target is specified, it will receive selected file data as a "src" attribute
to enable image previewing.
*/
export default class extends Controller {
  static targets = ['trigger', 'content', 'preview', 'defaultTemplate', 'successTemplate']

  static values = {
    accept: { type: String, default: '' },
    success: { type: Boolean, default: false }, // start out in "success" state
    defaultClasses: { type: String, default: '' },
    successClasses: { type: String, default: '' },
  }

  connect() {
    this.inputElement = this.element.querySelector("input[type='file']")
    if (!this.inputElement) {
      throw new Error("An <input type='file'> element is required")
    }

    if (this.successValue) this.setSuccessStatus()
    else this.setDefaultStatus()

    // forward events from the trigger to the hidden <input>
    if (this.hasTriggerTarget) {
      this.triggerTarget.addEventListener('click', () => {
        this.inputElement.click()
      })
    }

    // restrict accepted file types
    this.inputElement.setAttribute('accept', this.acceptValue)

    this.inputElement.addEventListener('change', (ev) => {
      // TODO: handle the case where the selected file is cleared out?

      this.setSuccessStatus()

      // populate the image preview if present
      if (this.hasPreviewTarget) {
        const file = ev.target.files[0]
        const reader = new FileReader()

        reader.readAsDataURL(file)
        reader.onload = () => {
          this.previewTarget.src = reader.result
          this.previewTarget.classList.remove('hidden')
        }
      }
    })
  }

  setDefaultStatus() {
    this.element.classList.remove(...this.classTokens(this.successClassesValue))
    this.element.classList.add(...this.classTokens(this.defaultClassesValue))

    if (this.hasContentTarget && this.hasDefaultTemplateTarget) {
      this.contentTarget.innerHTML = this.defaultTemplateTarget.innerHTML
    }
  }

  setSuccessStatus() {
    this.element.classList.remove(...this.classTokens(this.defaultClassesValue))
    this.element.classList.add(...this.classTokens(this.successClassesValue))

    if (this.hasContentTarget && this.hasSuccessTemplateTarget) {
      this.contentTarget.innerHTML = this.successTemplateTarget.innerHTML
    }
  }

  // classList.add/remove throw on empty strings, so drop blank tokens
  // (the class values are optional and default to "")
  classTokens(value) {
    return value.split(' ').filter(Boolean)
  }
}
